/**
 * HeadToHeadBar — You-vs-Them dex comparison. The headline shows Main / Shiny
 * / Lucky / 100%; the Compare page additionally opts into an expander that
 * opens out the remaining dexes, in Pokédex pill order.
 *
 * Shared by the Friends list (each live friend row) and the Compare page
 * header so the two surfaces read as one. Sources the counts-only
 * /dex-summary payload (the only dex data that crosses friend-to-friend) for
 * THEM, paired with the device-local deriveDexCounts for YOU. Stays silent
 * while loading and on a private / empty friend dex (privacy rule), so a
 * caller can mount it unconditionally.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { deriveDexCounts } from "@/lib/dexCounts";
import { DEX_STATE_EVENT } from "@/lib/dexStateStore";
import { DEX_TOTALS_EVENT } from "@/lib/dexQueryRefresh";
import {
  DEX_VARIANT_META,
  variantColor,
  type DexVariantKey,
} from "@/components/dexVariantVisuals";
import { ALL_DEX_VARIANTS_ORDERED } from "@/lib/dexVisibilityStore";

export type DexSummary = {
  caughtCount: number;
  shinyCount: number;
  luckyCount: number;
  perfectCount: number;
  // Size dexes. The endpoint (GET /api/friends/:id/dex-summary → projectSummary)
  // has always returned these; the type simply never declared them, so the
  // expanded view needed no server change. Optional so a cached response from
  // before this change still parses.
  xxsCount?: number;
  xxlCount?: number;
  // Migration 0038. Optional for the same reason: a response cached before the
  // column existed omits them, and a missing count must drop its row rather
  // than render a misleading zero.
  shadowCount?: number;
  purifiedCount?: number;
  megaCount?: number;
  gmaxCount?: number;
  shundoCount?: number;
  shinyLuckyCount?: number;
  topRegion: string | null;
};

// Reactive read of MY OWN dex counts from the device-local store, using the
// same deriveDexCounts source of truth as the Dex menu + Profile. Re-reads on
// every dex-state / dex-totals event so the head-to-head never goes stale
// after a scan.
export function useMyDexCounts() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setTick((n) => n + 1);
    window.addEventListener(DEX_TOTALS_EVENT, handler);
    window.addEventListener(DEX_STATE_EVENT, handler);
    return () => {
      window.removeEventListener(DEX_TOTALS_EVENT, handler);
      window.removeEventListener(DEX_STATE_EVENT, handler);
    };
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => deriveDexCounts(), [tick]);
}

/** One head-to-head row: my count, a split bar, their count, and the label. */
function H2HRow({
  label,
  mine,
  theirs,
  color,
  testId,
  countKey,
}: {
  label: string;
  mine: number;
  theirs: number;
  color: string;
  testId?: string;
  /**
   * Testid stem for the two numbers. Deliberately NOT derived from `testId`:
   * the expander asserts row ORDER by querying `[data-testid^='head-to-head-
   * row-']`, so anything nested that shares that prefix silently joins the row
   * list. (The bar fill already has to be filtered out of that query by hand —
   * one such trap is enough.)
   */
  countKey?: string;
}) {
  const total = Math.max(mine + theirs, 1);
  const minePct = (mine / total) * 100;
  const iLead = mine > theirs;
  const theyLead = theirs > mine;

  /**
   * Both sides now read in the SAME language: the dex's own colour, with WEIGHT
   * carrying the only thing that differs between them — who is ahead.
   *
   * It used to be two languages at once. Mine went dex-coloured when I led and
   * grey when I didn't; theirs went white when they led and grey when they
   * didn't; and both were permanently bold. So the row asked you to compare a
   * green number against a white one and work out which state each was in, and
   * bold — the loudest signal on the row — said nothing at all.
   *
   * Now exactly ONE number per row is bold and fully lit: the leader. A tie
   * leaves neither bold, which is the honest rendering of a tie (bolding both
   * would read as "everyone's winning"). Colour never moves, so a row still
   * says which dex it is at a glance.
   */
  const numberStyle = (leads: boolean, tied: boolean) => ({
    color,
    opacity: leads || tied ? 1 : 0.45,
    fontWeight: leads ? 800 : 500,
  });
  const tie = mine === theirs;

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span
        className="tabular-nums text-[11px] w-10 text-right"
        style={numberStyle(iLead, tie)}
        data-leader={iLead || undefined}
        data-testid={countKey ? `head-to-head-num-${countKey}-mine` : undefined}
      >
        {mine}
      </span>
      <div className="flex-1 h-2 rounded-full overflow-hidden flex" style={{ background: "var(--surface-raised)" }}>
        <span
          style={{ width: `${minePct}%`, background: color, opacity: 0.9 }}
          data-testid={testId ? `${testId}-fill` : undefined}
        />
        <span style={{ width: `${100 - minePct}%`, background: "var(--text-ink-faint)", opacity: 0.35 }} />
      </div>
      <span
        className="tabular-nums text-[11px] w-10"
        style={numberStyle(theyLead, tie)}
        data-leader={theyLead || undefined}
        data-testid={countKey ? `head-to-head-num-${countKey}-theirs` : undefined}
      >
        {theirs}
      </span>
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground w-10 shrink-0">{label}</span>
    </div>
  );
}

/**
 * How to read each dex's two counts. Keyed to the shared variant visual
 * language so every bar carries the SAME colour as its filter pill
 * (DEX_VARIANT_META → variantColor).
 *
 * A row needs a count for BOTH trainers. Mine come from the device-local store
 * (composedVariantSummary handles any variant); theirs come from the
 * counts-only friend summary, the only dex data that crosses friend-to-friend.
 * A dex the summary cannot report is dropped rather than drawn as a misleading
 * zero — "they have none" and "we cannot see it" must not look the same.
 */
const DEX_ACCESSORS: Record<string, {
  mine: (c: ReturnType<typeof useMyDexCounts>) => number;
  theirs: (s: DexSummary) => number | undefined;
}> = {
  pokemon:    { mine: (c) => c.main,       theirs: (s) => s.caughtCount },
  shiny:      { mine: (c) => c.shiny,      theirs: (s) => s.shinyCount },
  lucky:      { mine: (c) => c.lucky,      theirs: (s) => s.luckyCount },
  perfect:    { mine: (c) => c.perfect,    theirs: (s) => s.perfectCount },
  shundo:     { mine: (c) => c.shundo,     theirs: (s) => s.shundoCount },
  shinyLucky: { mine: (c) => c.shinyLucky, theirs: (s) => s.shinyLuckyCount },
  xxl:        { mine: (c) => c.xxl,        theirs: (s) => s.xxlCount },
  xxs:        { mine: (c) => c.xxs,        theirs: (s) => s.xxsCount },
  shadow:     { mine: (c) => c.shadow,     theirs: (s) => s.shadowCount },
  purified:   { mine: (c) => c.purified,   theirs: (s) => s.purifiedCount },
  mega:       { mine: (c) => c.mega,       theirs: (s) => s.megaCount },
  gmax:       { mine: (c) => c.gmax,       theirs: (s) => s.gmaxCount },
};

/** The four the headline shows; the expander picks up from there. */
const HEADLINE_DEXES: readonly DexVariantKey[] = ["pokemon", "shiny", "lucky", "perfect"];

/**
 * The expander's rows, in the SAME order the dex pills use on the Pokédex page
 * (ALL_DEX_VARIANTS_ORDERED), so the two screens read as one product rather
 * than two orderings of the same list.
 *
 * Excludes the headline four — they are already on screen directly above, and
 * repeating them made the panel look like it had rendered twice. Also excludes
 * "complete", which is a derived rollup with no friend-side count.
 */
const EXPANDED_ORDER: readonly DexVariantKey[] = ALL_DEX_VARIANTS_ORDERED.filter(
  (v): v is DexVariantKey =>
    v !== "complete" &&
    !HEADLINE_DEXES.includes(v as DexVariantKey) &&
    v in DEX_ACCESSORS,
);

export function HeadToHeadBar({
  friendId,
  expandable = false,
}: {
  friendId: number;
  /**
   * Show the "All dexes" expander beneath the headline four. Opt-in so the
   * Friends list rows stay exactly as they are — only the Compare page asks
   * for it.
   */
  expandable?: boolean;
}) {
  const mine = useMyDexCounts();
  const [expanded, setExpanded] = useState(false);
  const summaryQ = useQuery<{ private: boolean; summary: DexSummary | null }>({
    queryKey: [`/api/friends/${friendId}/dex-summary`],
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const s = summaryQ.data?.summary;
  if (summaryQ.data?.private || !s || s.caughtCount === 0) return null;

  // Labels come from DEX_VARIANT_META so a dex is named the same everywhere.
  // The Main dex used to read "Caught" here and "Main" in the expander — one
  // dex, two names, on the same card.
  // `key` is the variant id, not the label: labels are display copy (and "100%"
  // is a poor thing to build a testid out of), so the identity a test or a
  // stylesheet keys off has to be the stable one.
  const rows: { key: DexVariantKey; label: string; mine: number; theirs: number; color: string }[] = [
    { key: "pokemon", label: DEX_VARIANT_META.pokemon.label, mine: mine.main,    theirs: s.caughtCount,  color: "var(--accent-fill)" },
    { key: "shiny",   label: DEX_VARIANT_META.shiny.label,   mine: mine.shiny,   theirs: s.shinyCount,   color: "hsl(var(--shiny))" },
    { key: "lucky",   label: DEX_VARIANT_META.lucky.label,   mine: mine.lucky,   theirs: s.luckyCount,   color: "hsl(var(--lucky))" },
    { key: "perfect", label: DEX_VARIANT_META.perfect.label, mine: mine.perfect, theirs: s.perfectCount, color: "hsl(var(--perfect))" },
  ];

  // Drop any dex the friend summary has no count for rather than showing 0.
  const expandedRows = EXPANDED_ORDER.flatMap((key) => {
    const acc = DEX_ACCESSORS[key];
    const theirs = acc.theirs(s);
    if (theirs == null) return [];
    return [{
      key,
      label: DEX_VARIANT_META[key].label,
      mine: acc.mine(mine),
      theirs,
      color: variantColor(key),
    }];
  });

  return (
    <div
      className="mt-2 rounded-xl p-2"
      style={{ background: "var(--surface-dark-7)", border: "1px solid var(--border-subtle)" }}
      data-testid={`friend-head-to-head-${friendId}`}
    >
      <div className="flex items-center justify-between px-1 mb-1.5">
        <span className="text-[9px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--accent-fill)" }}>You</span>
        <span className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">vs</span>
        <span className="text-[9px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Them</span>
      </div>
      {/* Headline four — unchanged. */}
      <div className="space-y-1.5">
        {rows.map((r) => (
          <H2HRow key={r.key} label={r.label} mine={r.mine} theirs={r.theirs} color={r.color} countKey={r.key} />
        ))}
      </div>

      {expandable && expandedRows.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold transition-colors"
            style={{
              background: expanded ? "var(--surface-raised)" : "transparent",
              border: "1px solid var(--border-subtle)",
              color: "var(--accent-fill)",
            }}
            data-testid={`head-to-head-expand-${friendId}`}
          >
            {expanded ? "Hide dexes" : "All dexes"}
            <ChevronDown
              className="w-3 h-3 transition-transform"
              style={{ transform: expanded ? "rotate(180deg)" : "none" }}
              aria-hidden="true"
            />
          </button>

          {expanded && (
            <div
              className="mt-2 pt-2 space-y-1.5"
              style={{ borderTop: "1px solid var(--border-subtle)" }}
              data-testid={`head-to-head-all-dexes-${friendId}`}
            >
              {expandedRows.map((r) => (
                <H2HRow
                  key={r.key}
                  label={r.label}
                  mine={r.mine}
                  theirs={r.theirs}
                  color={r.color}
                  testId={`head-to-head-row-${r.key}`}
                  countKey={r.key}
                />
              ))}
            </div>
          )}
        </>
      )}

    </div>
  );
}
